import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';
import fieldConstants from '../constants/rmaFieldConstants.json';

const API_BASE = process.env.REACT_APP_API_URL;

const DEFECT_TAGS = fieldConstants.imageDefectTags || [];
const AREA_TAGS   = fieldConstants.imageAreaTags   || [];
const ALL_KNOWN_TAGS = new Set([...DEFECT_TAGS.map(t => t.value), ...AREA_TAGS.map(t => t.value)]);

function resolveUrl(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
}

function isImage(mime, url) {
  if (mime && mime.startsWith('image/')) return true;
  const ext = (url || '').split('.').pop().toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
}

export default function SubmissionImageGallery({ images, rmaId, canCategorize }) {
  const [localImages, setLocalImages] = useState(images || []);
  const [lightbox, setLightbox] = useState(null);
  const [editTags, setEditTags] = useState([]);
  const [editCaption, setEditCaption] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const { getToken } = useAuth();

  useEffect(() => { setLocalImages(images || []); }, [images]);

  const imageFiles = localImages.filter(f => isImage(f.mime, f.url));
  const otherFiles = localImages.filter(f => !isImage(f.mime, f.url));

  const openLightbox = (idx) => {
    setLightbox(idx);
    setEditTags([...(imageFiles[idx]?.tags || [])]);
    setEditCaption(imageFiles[idx]?.caption || '');
    setSavedAt(null);
  };

  const closeLightbox = () => setLightbox(null);
  const prev = () => { const i = (lightbox - 1 + imageFiles.length) % imageFiles.length; openLightbox(i); };
  const next = () => { const i = (lightbox + 1) % imageFiles.length; openLightbox(i); };

  const toggleTag = (tag) => {
    setEditTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const saveCategories = async () => {
    if (!rmaId || !canCategorize) return;
    const img = imageFiles[lightbox];
    if (!img) return;
    setSaving(true);
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/rma/${rmaId}/images/tag`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url: img.url, tags: editTags, caption: editCaption }),
      });
      setLocalImages(prev => prev.map(im =>
        im.url === img.url ? { ...im, tags: editTags, caption: editCaption } : im
      ));
      setSavedAt(Date.now());
    } catch (err) {
      console.error('[gallery] save tags error', err);
    } finally {
      setSaving(false);
    }
  };

  if (!localImages || localImages.length === 0) return null;

  return (
    <div style={{ marginTop: '1rem' }}>
      <h3 style={{ margin: '0 0 0.6rem', fontSize: 13, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
        Submission photos ({localImages.length})
      </h3>

      {imageFiles.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {imageFiles.map((file, idx) => (
            <div
              key={file.url || idx}
              onClick={() => openLightbox(idx)}
              style={{
                width: 80, height: 80, borderRadius: 8, overflow: 'hidden',
                cursor: 'pointer', border: `1px solid ${colors.panelBorder}`,
                flexShrink: 0, position: 'relative',
              }}
            >
              <img src={resolveUrl(file.url)} alt={file.name || `Photo ${idx + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              {file.tags?.length > 0 && (
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'rgba(0,0,0,0.6)', padding: '2px 4px',
                  fontSize: 9, color: '#46bb89', textAlign: 'center',
                  overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                }}>
                  {file.tags[0]}{file.tags.length > 1 ? ` +${file.tags.length - 1}` : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {otherFiles.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {otherFiles.map((file, idx) => (
            <a key={file.url || idx} href={resolveUrl(file.url)} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 12, color: colors.primary, textDecoration: 'none' }}>
              📎 {file.name || `Attachment ${idx + 1}`}
            </a>
          ))}
        </div>
      )}

      {lightbox !== null && createPortal(
        <div onClick={closeLightbox} style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(0,0,0,0.95)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          overflowY: 'auto',
        }}>
          <button onClick={e => { e.stopPropagation(); closeLightbox(); }} style={closeBtn}>✕</button>

          {imageFiles.length > 1 && (
            <button onClick={e => { e.stopPropagation(); prev(); }} style={{ ...navBtn, left: 16 }}>‹</button>
          )}

          <div onClick={e => e.stopPropagation()} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            maxWidth: 860, width: '100%', padding: '48px 20px 24px',
          }}>
            <img
              src={resolveUrl(imageFiles[lightbox]?.url)}
              alt={imageFiles[lightbox]?.name || ''}
              style={{ maxWidth: '100%', maxHeight: '55vh', borderRadius: 8, objectFit: 'contain', display: 'block', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
            />

            <div style={{ color: '#aaa', fontSize: 12, marginTop: 8 }}>
              {lightbox + 1} / {imageFiles.length}
              {imageFiles[lightbox]?.name ? ` — ${imageFiles[lightbox].name}` : ''}
            </div>

            {canCategorize && (
              <div style={{
                marginTop: 20, width: '100%',
                background: 'var(--ni-panel-raised, #1D2522)',
                border: '1px solid rgba(70,187,137,0.2)',
                borderRadius: 10, padding: '16px 20px',
              }}>
                <div style={{ fontSize: 11, color: 'var(--ni-green, #46bb89)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14, fontWeight: 700 }}>
                  Categorize this image
                </div>

                {/* Defect type chips */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600, marginBottom: 7 }}>
                    Defect type
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {DEFECT_TAGS.map(({ value, label }) => {
                      const active = editTags.includes(value);
                      return (
                        <button key={value} onClick={() => toggleTag(value)} style={{
                          padding: '4px 11px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                          border: `1px solid ${active ? 'var(--ni-green, #46bb89)' : 'rgba(255,255,255,0.1)'}`,
                          background: active ? 'rgba(70,187,137,0.18)' : 'rgba(255,255,255,0.03)',
                          color: active ? 'var(--ni-green, #46bb89)' : colors.textMuted,
                          fontWeight: active ? 700 : 400,
                          transition: 'all 0.13s',
                        }}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Area chips */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600, marginBottom: 7 }}>
                    Area / component
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {AREA_TAGS.map(({ value, label }) => {
                      const active = editTags.includes(value);
                      return (
                        <button key={value} onClick={() => toggleTag(value)} style={{
                          padding: '4px 11px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                          border: `1px solid ${active ? 'rgba(70,187,137,0.7)' : 'rgba(255,255,255,0.1)'}`,
                          background: active ? 'rgba(70,187,137,0.12)' : 'rgba(255,255,255,0.03)',
                          color: active ? 'var(--ni-green, #46bb89)' : colors.textMuted,
                          fontWeight: active ? 700 : 400,
                          transition: 'all 0.13s',
                        }}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Legacy free-text tags still on this image — show as removable, but can't add new ones */}
                {editTags.filter(t => !ALL_KNOWN_TAGS.has(t)).length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600, marginBottom: 7 }}>
                      Legacy tags (click × to remove)
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {editTags.filter(t => !ALL_KNOWN_TAGS.has(t)).map(tag => (
                        <span key={tag} style={{
                          padding: '3px 9px', borderRadius: 20, fontSize: 12,
                          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
                          color: colors.textMuted, display: 'flex', alignItems: 'center', gap: 5,
                        }}>
                          {tag}
                          <span onClick={() => setEditTags(p => p.filter(t2 => t2 !== tag))}
                            style={{ cursor: 'pointer', opacity: 0.6, fontSize: 14, lineHeight: 1 }}>×</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Caption */}
                <textarea
                  value={editCaption}
                  onChange={e => setEditCaption(e.target.value)}
                  placeholder="Describe what's visible — location, severity, any context (optional but recommended)…"
                  rows={2}
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 6, padding: '7px 10px',
                    color: colors.textStrong, fontSize: 13,
                    resize: 'vertical', outline: 'none', boxSizing: 'border-box', marginBottom: 12,
                  }}
                />

                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button onClick={saveCategories} disabled={saving} style={{
                    background: 'var(--ni-green, #46bb89)', border: 'none', borderRadius: 6,
                    padding: '7px 22px', color: '#0B0F0D', fontWeight: 700, fontSize: 13,
                    cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
                  }}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  {savedAt && (
                    <span style={{ fontSize: 12, color: 'var(--ni-green, #46bb89)' }}>✓ Saved</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {imageFiles.length > 1 && (
            <button onClick={e => { e.stopPropagation(); next(); }} style={{ ...navBtn, right: 16 }}>›</button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

const closeBtn = {
  position: 'fixed', top: 16, right: 20,
  background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(147,149,151,0.25)',
  color: 'var(--ni-text, #E1E6E2)', fontSize: 20, cursor: 'pointer', zIndex: 1,
  borderRadius: 8, padding: '4px 10px', lineHeight: 1,
};

const navBtn = {
  position: 'fixed',
  background: 'rgba(70,187,137,0.12)', border: '1px solid rgba(70,187,137,0.3)',
  color: 'var(--ni-green, #46bb89)',
  fontSize: 36, cursor: 'pointer', borderRadius: 8, padding: '0.25rem 0.6rem',
  top: '50%', transform: 'translateY(-50%)',
};
